import { useMemo, useCallback } from "react";
import { useUserSettings } from "./useUserSettings";
import {
  effectiveTier as resolveEffectiveTier,
  canUseFeature as canUse,
  quotaFor as quota,
} from "../lib/tier-config";

/**
 * useEntitlements — the client read path for the current user's tier.
 *
 * Reads `subscription` off the existing useUserSettings() doc subscription (NO
 * second Firestore listener), computes effectiveTier via the shared tier-config,
 * and exposes the entitlement helpers the UI gates on.
 *
 * effectiveTier is the single value all gating uses; it is "free" whenever the
 * subscription is paused/lapsed/past-due/canceled (see tier-config.effectiveTier).
 *
 * @returns {{
 *   loading: boolean,
 *   tier: string,            // stored tier (may differ from effective when lapsed)
 *   status: string,
 *   effectiveTier: string,   // gate on this
 *   currentPeriodEnd: any,
 *   cancelAtPeriodEnd: boolean,
 *   paused: boolean,         // graceful-downgrade marker (pausedAt set)
 *   canUseFeature: (featureId: string) => boolean,
 *   quotaFor: (key: string) => number|null,
 * }}
 */
export function useEntitlements() {
  const { subscription, loading } = useUserSettings();

  const sub = useMemo(
    () => subscription || { tier: "free", status: "active" },
    [subscription]
  );
  const tier = sub.tier || "free";
  const status = sub.status || "active";
  const paused = sub.pausedAt != null;
  const effectiveTier = useMemo(() => resolveEffectiveTier(sub), [sub]);

  const canUseFeature = useCallback(
    (featureId) => canUse(effectiveTier, featureId),
    [effectiveTier]
  );

  const quotaFor = useCallback((key) => quota(effectiveTier, key), [effectiveTier]);

  return {
    loading: Boolean(loading),
    tier,
    status,
    effectiveTier,
    currentPeriodEnd: sub.currentPeriodEnd ?? null,
    cancelAtPeriodEnd: sub.cancelAtPeriodEnd === true,
    paused,
    canUseFeature,
    quotaFor,
  };
}
